import { useState, useEffect } from "react";
import { Calendar } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  type TimePreset,
  TIME_PRESET_ORDER,
  TIME_PRESET_LABELS,
  DEFAULT_TIME_PRESET,
  getDateRangeFromPreset,
  formatDateRangeDisplay,
  type TimePeriodRange,
} from "@/lib/timePeriodUtils";
import { cn } from "@/lib/utils";

/** Value emitted by TimePeriodFilter; compatible with DateFilterValue (startDate/endDate). */
export interface TimePeriodValue {
  preset: TimePreset;
  startDate: string;
  endDate: string;
}

interface TimePeriodFilterProps {
  value?: TimePeriodValue | null;
  onChange: (value: TimePeriodValue) => void;
  defaultPreset?: TimePreset;
  className?: string;
}

export const TimePeriodFilter = ({
  value,
  onChange,
  defaultPreset = DEFAULT_TIME_PRESET,
  className,
}: TimePeriodFilterProps) => {
  const [preset, setPreset] = useState<TimePreset>(value?.preset ?? defaultPreset);
  const [range, setRange] = useState<TimePeriodRange>(() =>
    value?.startDate && value?.endDate
      ? { start: value.startDate, end: value.endDate }
      : getDateRangeFromPreset(value?.preset ?? defaultPreset)
  );

  const isControlled = value != null;

  useEffect(() => {
    if (isControlled && value) {
      setPreset(value.preset);
      setRange({ start: value.startDate, end: value.endDate });
    }
  }, [isControlled, value?.preset, value?.startDate, value?.endDate]);

  const syncAndNotify = (newPreset: TimePreset, newRange: TimePeriodRange) => {
    setPreset(newPreset);
    setRange(newRange);
    onChange({
      preset: newPreset,
      startDate: newRange.start,
      endDate: newRange.end,
    });
  };

  const handlePresetChange = (newPreset: TimePreset) => {
    if (newPreset === "custom") {
      setPreset("custom");
      return;
    }
    const newRange = getDateRangeFromPreset(newPreset);
    syncAndNotify(newPreset, newRange);
  };

  const handleCustomRangeChange = (newRange: TimePeriodRange) => {
    setRange(newRange);
    onChange({
      preset: "custom",
      startDate: newRange.start,
      endDate: newRange.end,
    });
  };

  // Initial emit when uncontrolled (default preset)
  useEffect(() => {
    if (!isControlled) {
      const initialRange = getDateRangeFromPreset(defaultPreset);
      onChange({
        preset: defaultPreset,
        startDate: initialRange.start,
        endDate: initialRange.end,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const displayRange = range.start && range.end ? range : getDateRangeFromPreset(preset);
  const dateDisplayText = formatDateRangeDisplay(displayRange.start, displayRange.end);

  return (
    <div className={cn("flex items-center gap-2 flex-wrap", className)}>
      <Label className="text-xs text-muted-foreground font-normal whitespace-nowrap flex items-center gap-1.5">
        <Calendar className="h-3.5 w-3.5" />
        Time Period
      </Label>
      <Select value={preset} onValueChange={(v) => handlePresetChange(v as TimePreset)}>
        <SelectTrigger className="h-8 w-[180px] rounded-full border border-border bg-background px-3 py-1.5 text-xs font-normal">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {TIME_PRESET_ORDER.map((p) => (
            <SelectItem key={p} value={p}>
              {TIME_PRESET_LABELS[p]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <span className="text-xs text-muted-foreground whitespace-nowrap" aria-hidden>
        {dateDisplayText}
      </span>
      {preset === "custom" && (
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="h-8 rounded-full border border-border bg-background px-3 py-1.5 text-xs font-normal"
            >
              {displayRange.start} – {displayRange.end}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-4" align="start">
            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-1.5">
                <Label className="text-xs">From</Label>
                <Input
                  type="date"
                  value={range.start}
                  onChange={(e) =>
                    handleCustomRangeChange({ ...range, start: e.target.value })
                  }
                  className="h-8 text-xs"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label className="text-xs">To</Label>
                <Input
                  type="date"
                  value={range.end}
                  onChange={(e) =>
                    handleCustomRangeChange({ ...range, end: e.target.value })
                  }
                  className="h-8 text-xs"
                />
              </div>
            </div>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
};
